/**
 * @element qds-checkbox
 *
 * Binary and indeterminate choice control.
 *
 * Use it for an independent on/off choice, opting into something, or row
 * selection in a data table. Do NOT use it for mutually exclusive options
 * (use `qds-radio-group`) or for an immediate settings toggle that applies
 * without a save step — that is a switch (`qds-theme-switch` is the existing
 * precedent). A checkbox implies a pending change; a switch implies an
 * applied one.
 *
 * @attr {boolean} checked - Whether the checkbox is checked. Reflected.
 * @attr {boolean} indeterminate - Whether the checkbox shows the indeterminate bar. Set by consumer only; cleared by user interaction.
 * @attr {string} value - Value submitted when checked. Default "on".
 * @attr {string} label - Label text. Also settable as slotted text content.
 * @attr {string} hint - Helper text below the label.
 * @attr {string} error-text - Error message shown when `invalid` is true.
 * @attr {string} name - Form field name.
 * @attr {'sm'|'md'|'lg'} size - Control size. Default "md".
 * @attr {boolean} disabled - Disables interaction and dims the control.
 * @attr {boolean} readonly - Checked state holds; no pointer or key response; no dimming.
 * @attr {boolean} required - Must be checked to be valid (valueMissing while unchecked).
 * @attr {boolean} invalid - Author-forced invalid state.
 *
 * @csspart container - The outer wrapper.
 * @csspart row - The clickable row (hit area).
 * @csspart box - The checkbox square.
 * @csspart check - The check glyph (inline SVG).
 * @csspart indeterminate-bar - The indeterminate bar (inline SVG).
 * @csspart label - The label text.
 * @csspart hint - The helper text.
 * @csspart error - The error message.
 *
 * @fires change - When checked state is committed. Detail: `{ component, checked, value }`.
 * @fires input - Same moment as change; native parity. Detail: `{ component, checked, value }`.
 * @fires qds-validity-change - When validity transitions. Detail: `QdsValidityChangeEventDetail`.
 *
 * @example
 * <qds-checkbox label="Accept terms" required></qds-checkbox>
 * <qds-checkbox checked label="Enable notifications"></qds-checkbox>
 * <qds-checkbox indeterminate label="Select all"></qds-checkbox>
 */

import { loadBaseCSS } from '../shared/button-utils.js';
import {
  attachInternalsSafe,
  ValidityTracker,
  dispatchValidityChange,
  generateControlId,
  isAttrTruthy,
  reflectBoolean,
  parseSize,
} from '../shared/form-control.js';
import type { QdsControlSize, QdsValidity, QdsValidityChangeEventDetail } from '../../types/index';

interface CheckboxChangeEventDetail {
  component: HTMLElement;
  checked: boolean;
  value: string;
}

export class QdsCheckbox extends HTMLElement {
  static formAssociated = true;
  static qdsFieldConsumer = true;

  static get observedAttributes(): string[] {
    return [
      'checked',
      'indeterminate',
      'value',
      'label',
      'hint',
      'error-text',
      'name',
      'size',
      'disabled',
      'readonly',
      'required',
      'invalid',
    ];
  }

  private _internals?: ElementInternals;
  private _validity: ValidityTracker;

  private _checked: boolean = false;
  private _indeterminate: boolean = false;
  private _value: string = 'on';
  private _label: string = '';
  private _hint: string = '';
  private _errorText: string = '';
  private _name: string = '';
  private _size: QdsControlSize = 'md';
  private _disabled: boolean = false;
  private _formDisabled = false;
  private _readonly: boolean = false;
  private _required: boolean = false;
  private _invalid: boolean = false;

  /** Initial checked state for formResetCallback. */
  private _initialChecked: boolean = false;

  private _labelId: string;
  private _hintId: string;
  private _errorId: string;

  private _clickHandler: ((e: MouseEvent) => void) | null = null;
  private _keydownHandler: ((e: KeyboardEvent) => void) | null = null;
  private _focusHandler: (() => void) | null = null;
  private _blurHandler: (() => void) | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    const internals = attachInternalsSafe(this);
    // jsdom's attachInternals returns an object but does not implement
    // setFormValue or validity. Pass undefined so ValidityTracker uses its
    // QdsValidity fallback instead of crashing on undefined validity.
    if (internals && typeof internals.setFormValue === 'function') {
      this._internals = internals;
    }
    this._validity = new ValidityTracker(this._internals);
    this._labelId = generateControlId('checkbox-label');
    this._hintId = generateControlId('checkbox-hint');
    this._errorId = generateControlId('checkbox-error');
    loadBaseCSS();
  }

  // ── Properties ──────────────────────────────────────────────────────

  get checked(): boolean {
    return this._checked;
  }

  set checked(value: boolean) {
    const val = Boolean(value);
    if (this._checked === val) return;
    this._checked = val;
    reflectBoolean(this, 'checked', val);
    this._updateFormValue();
    this._updateValidity();
    if (this.isConnected) this.render();
  }

  get indeterminate(): boolean {
    return this._indeterminate;
  }

  set indeterminate(value: boolean) {
    const val = Boolean(value);
    if (this._indeterminate === val) return;
    this._indeterminate = val;
    reflectBoolean(this, 'indeterminate', val);
    if (this.isConnected) this.render();
  }

  get value(): string {
    return this._value;
  }

  set value(value: string) {
    this._value = String(value);
    this._updateFormValue();
  }

  get label(): string {
    return this._label;
  }

  set label(value: string) {
    this._label = String(value);
    this.setAttribute('label', this._label);
    if (this.isConnected) this.render();
  }

  get hint(): string {
    return this._hint;
  }

  set hint(value: string) {
    this._hint = String(value);
    this.setAttribute('hint', this._hint);
    if (this.isConnected) this.render();
  }

  get errorText(): string {
    return this._errorText;
  }

  set errorText(value: string) {
    this._errorText = String(value);
    this.setAttribute('error-text', this._errorText);
    if (this.isConnected) this.render();
  }

  get name(): string {
    return this._name;
  }

  set name(value: string) {
    this._name = String(value);
    this.setAttribute('name', this._name);
  }

  get size(): QdsControlSize {
    return this._size;
  }

  set size(value: QdsControlSize) {
    this._size = parseSize(value);
    this.setAttribute('size', this._size);
    if (this.isConnected) this.render();
  }

  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    reflectBoolean(this, 'disabled', value);
  }

  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(value: boolean) {
    this._readonly = Boolean(value);
    reflectBoolean(this, 'readonly', this._readonly);
    if (this.isConnected) this.render();
  }

  get required(): boolean {
    return this._required;
  }

  set required(value: boolean) {
    this._required = Boolean(value);
    reflectBoolean(this, 'required', this._required);
    this._updateValidity();
    if (this.isConnected) this.render();
  }

  get invalid(): boolean {
    return this._invalid;
  }

  set invalid(value: boolean) {
    this._invalid = Boolean(value);
    reflectBoolean(this, 'invalid', this._invalid);
    if (this.isConnected) this.render();
  }

  get validity(): QdsValidity {
    return this._validity.validity;
  }

  // ── Lifecycle ───────────────────────────────────────────────────────

  connectedCallback(): void {
    if (!this.isConnected) return;

    // Sync from attributes (initial state).
    this._checked = this.hasAttribute('checked');
    this._indeterminate = this.hasAttribute('indeterminate');
    this._value = this.getAttribute('value') || 'on';
    this._label = this.getAttribute('label') || '';
    this._hint = this.getAttribute('hint') || '';
    this._errorText = this.getAttribute('error-text') || '';
    this._name = this.getAttribute('name') || '';
    this._size = parseSize(this.getAttribute('size'));
    this._disabled = this._formDisabled || isAttrTruthy(this.getAttribute('disabled'));
    this._readonly = isAttrTruthy(this.getAttribute('readonly'));
    this._required = isAttrTruthy(this.getAttribute('required'));
    this._invalid = isAttrTruthy(this.getAttribute('invalid'));

    this._initialChecked = this._checked;

    this._updateFormValue();
    this._updateValidity();
    this.render();
  }

  disconnectedCallback(): void {
    this._removeListeners();
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'checked':
        this._checked = newValue !== null && newValue !== 'false';
        this._updateFormValue();
        this._updateValidity();
        break;
      case 'indeterminate':
        this._indeterminate = newValue !== null && newValue !== 'false';
        break;
      case 'value':
        this._value = newValue || 'on';
        this._updateFormValue();
        break;
      case 'label':
        this._label = newValue || '';
        break;
      case 'hint':
        this._hint = newValue || '';
        break;
      case 'error-text':
        this._errorText = newValue || '';
        break;
      case 'name':
        this._name = newValue || '';
        break;
      case 'size':
        this._size = parseSize(newValue);
        break;
      case 'disabled':
        this._disabled = this._formDisabled || (newValue !== null && newValue !== 'false');
        break;
      case 'readonly':
        this._readonly = newValue !== null && newValue !== 'false';
        break;
      case 'required':
        this._required = newValue !== null && newValue !== 'false';
        this._updateValidity();
        break;
      case 'invalid':
        this._invalid = newValue !== null && newValue !== 'false';
        break;
    }

    if (this.isConnected) this.render();
  }

  // ── Form association ────────────────────────────────────────────────

  private _updateFormValue(): void {
    if (this._internals && typeof this._internals.setFormValue === 'function') {
      try {
        this._internals.setFormValue(this._checked ? this._value : null);
      } catch {
        /* noop — jsdom does not implement setFormValue */
      }
    }
  }

  private _updateValidity(): void {
    const wasValid = this._validity.valid;

    if (this._required && !this._checked) {
      this._validity.setValidity(
        { valueMissing: true, valid: false },
        'Please check this box to continue.',
      );
    } else {
      this._validity.setValidity({ valueMissing: false, valid: true }, '');
    }

    if (wasValid !== this._validity.valid) {
      dispatchValidityChange(this, this._validity);
    }
  }

  checkValidity(): boolean {
    return this._validity.checkValidity();
  }

  reportValidity(): boolean {
    return this._validity.reportValidity();
  }

  setCustomValidity(message: string): void {
    const wasValid = this._validity.valid;
    this._validity.setCustomValidity(message);
    if (wasValid !== this._validity.valid) {
      dispatchValidityChange(this, this._validity);
    }
  }

  formResetCallback(): void {
    this._checked = this._initialChecked;
    reflectBoolean(this, 'checked', this._checked);
    this._indeterminate = false;
    reflectBoolean(this, 'indeterminate', false);
    this._updateFormValue();
    this._updateValidity();
    this.render();
  }

  formDisabledCallback(disabled: boolean): void {
    this._formDisabled = disabled;
    this._disabled = disabled || isAttrTruthy(this.getAttribute('disabled'));
    if (this.isConnected) this.render();
  }

  // ── Interaction ─────────────────────────────────────────────────────

  private _toggle(): void {
    if (this._disabled || this._readonly) return;

    // Indeterminate → checked, clear indeterminate.
    if (this._indeterminate) {
      this._indeterminate = false;
      reflectBoolean(this, 'indeterminate', false);
      this._checked = true;
    } else {
      this._checked = !this._checked;
    }

    reflectBoolean(this, 'checked', this._checked);
    this._updateFormValue();
    this._updateValidity();
    this.render();

    const detail: CheckboxChangeEventDetail = {
      component: this,
      checked: this._checked,
      value: this._value,
    };

    this.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail,
    }));

    this.dispatchEvent(new CustomEvent('change', {
      bubbles: true,
      composed: true,
      detail,
    }));
  }

  // ── Event listeners ─────────────────────────────────────────────────

  private _removeListeners(): void {
    if (this._clickHandler) {
      this.removeEventListener('click', this._clickHandler);
      this._clickHandler = null;
    }
    if (this._keydownHandler) {
      this.removeEventListener('keydown', this._keydownHandler);
      this._keydownHandler = null;
    }
    if (this._focusHandler) {
      this.removeEventListener('focus', this._focusHandler);
      this._focusHandler = null;
    }
    if (this._blurHandler) {
      this.removeEventListener('blur', this._blurHandler);
      this._blurHandler = null;
    }
  }

  // ── Render ──────────────────────────────────────────────────────────

  private _escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private render(): void {
    if (!this.shadowRoot) return;

    this._removeListeners();

    const boxSize =
      this._size === 'sm' ? '16px'
      : this._size === 'lg' ? '20px'
      : '18px';

    const rowMinHeight =
      this._size === 'sm' ? '28px'
      : this._size === 'lg' ? '36px'
      : '32px';

    const fontSize =
      this._size === 'sm' ? '14px'
      : this._size === 'lg' ? '16px'
      : '15px';

    const ariaChecked = this._indeterminate ? 'mixed'
      : this._checked ? 'true' : 'false';

    const tabIndex = this._disabled ? '-1' : '0';

    const describeBy: string[] = [];
    if (this._hint) describeBy.push(this._hintId);
    if (this._invalid && this._errorText) describeBy.push(this._errorId);

    const showCheck = this._checked && !this._indeterminate;
    const showIndeterminate = this._indeterminate;
    const showError = this._invalid && this._errorText !== '';
    const showHint = this._hint !== '' && !showError;

    // Slotted text content takes precedence for label if no label attribute.
    const useSlotForLabel = this._label === '' && this.textContent?.trim().length;
    const labelText = this._label !== '' ? this._escapeHtml(this._label) : '';

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-block;
          --qds-checkbox-size: ${boxSize};
          --qds-checkbox-radius: var(--theme-radius-sm, 4px);
          --qds-checkbox-checked-background: var(--theme-accent-primary, #445E89);
          --qds-checkbox-border-color: var(--theme-border-secondary, #C5CEE0);
          --qds-checkbox-gap: var(--theme-space-2, 8px);
          color: var(--theme-text-primary, #333);
          font-family: inherit;
          font-size: ${fontSize};
          outline: none;
        }

        :host([disabled]), :host(:disabled) {
          opacity: 0.55;
          cursor: not-allowed;
          pointer-events: none;
        }

        :host([readonly]) {
          cursor: default;
        }

        :host(:focus-visible) .box {
          box-shadow: 0 0 0 2px var(--button-focus-ring, rgba(68, 94, 137, 0.25));
        }

        .container {
          display: block;
        }

        .row {
          display: flex;
          align-items: flex-start;
          gap: var(--qds-checkbox-gap);
          min-height: ${rowMinHeight};
          padding: 2px 0;
          box-sizing: border-box;
          cursor: pointer;
          border-radius: var(--qds-checkbox-radius);
          transition: background-color var(--transition-speed-fast, 0.15s);
        }

        :host([disabled]) .row, :host(:disabled) .row,
        :host([readonly]) .row {
          cursor: default;
        }

        .row:hover {
          background-color: var(--button-hover-bg, rgba(0, 0, 0, 0.05));
        }

        :host([disabled]) .row:hover, :host(:disabled) .row:hover,
        :host([readonly]) .row:hover {
          background-color: transparent;
        }

        .box {
          flex-shrink: 0;
          width: var(--qds-checkbox-size);
          height: var(--qds-checkbox-size);
          min-width: var(--qds-checkbox-size);
          border: 1.5px solid var(--qds-checkbox-border-color);
          border-radius: var(--qds-checkbox-radius);
          background: var(--theme-background-primary, #fff);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 1px;
          box-sizing: border-box;
          transition: border-color var(--transition-speed-fast, 0.15s),
                      background-color var(--transition-speed-fast, 0.15s),
                      box-shadow var(--transition-speed-fast, 0.15s);
        }

        .row:hover .box {
          border-color: var(--theme-border-strong, #AFBBD1);
        }

        :host([invalid]) .box {
          border-color: var(--theme-error, #8B1F00);
        }

        :host([invalid]) .row:hover .box {
          border-color: var(--theme-error, #8B1F00);
        }

        .box.checked {
          background: var(--qds-checkbox-checked-background);
          border-color: var(--qds-checkbox-checked-background);
        }

        .check {
          color: var(--theme-text-on-accent-primary, #fff);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }

        .check svg {
          width: 100%;
          height: 100%;
        }

        .indeterminate-bar {
          color: var(--theme-text-on-accent-primary, #fff);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }

        .indeterminate-bar svg {
          width: 70%;
          height: 70%;
        }

        .label {
          color: var(--theme-text-primary, #333);
          line-height: 1.4;
          padding-top: 1px;
        }

        .hint {
          color: var(--theme-text-tertiary, #6A6A6A);
          font-size: 13px;
          line-height: 1.4;
          margin-top: var(--theme-space-1, 4px);
          margin-left: calc(var(--qds-checkbox-size) + var(--qds-checkbox-gap));
        }

        .error {
          color: var(--theme-error, #8B1F00);
          font-size: 13px;
          line-height: 1.4;
          margin-top: var(--theme-space-1, 4px);
          margin-left: calc(var(--qds-checkbox-size) + var(--qds-checkbox-gap));
        }

        @media (pointer: coarse) {
          .row {
            min-height: 44px;
          }
        }
      </style>

      <div part="container" class="container">
        <div part="row" class="row" id="checkbox-row">
          <div part="box" class="box${showCheck ? ' checked' : ''}${showIndeterminate ? ' checked' : ''}" id="checkbox-box">
            ${
              showCheck
                ? `<span part="check" class="check">
                     <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                       <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                     </svg>
                   </span>`
                : ''
            }
            ${
              showIndeterminate
                ? `<span part="indeterminate-bar" class="indeterminate-bar">
                     <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                       <path d="M3.5 8H12.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                     </svg>
                   </span>`
                : ''
            }
          </div>
          ${
            labelText || useSlotForLabel
              ? `<span part="label" class="label" id="${this._labelId}">${labelText}${
                  useSlotForLabel ? '<slot></slot>' : ''
                }</span>`
              : ''
          }
        </div>
        ${
          showHint
            ? `<div part="hint" class="hint" id="${this._hintId}">${this._escapeHtml(this._hint)}</div>`
            : ''
        }
        ${
          showError
            ? `<div part="error" class="error" id="${this._errorId}">${this._escapeHtml(this._errorText)}</div>`
            : ''
        }
      </div>
    `;

    // Wire up ARIA on the host.
    this.setAttribute('role', 'checkbox');
    this.setAttribute('aria-checked', ariaChecked);
    this.setAttribute('tabindex', tabIndex);

    if (this._label || useSlotForLabel) {
      this.setAttribute('aria-labelledby', this._labelId);
    } else {
      this.removeAttribute('aria-labelledby');
    }

    if (describeBy.length > 0) {
      this.setAttribute('aria-describedby', describeBy.join(' '));
    } else {
      this.removeAttribute('aria-describedby');
    }

    if (this._required) {
      this.setAttribute('aria-required', 'true');
    } else {
      this.removeAttribute('aria-required');
    }

    if (this._invalid) {
      this.setAttribute('aria-invalid', 'true');
    } else {
      this.removeAttribute('aria-invalid');
    }

    if (this._disabled) {
      this.setAttribute('aria-disabled', 'true');
    } else {
      this.removeAttribute('aria-disabled');
    }

    // Attach event listeners.
    this._clickHandler = (e: MouseEvent): void => {
      e.preventDefault();
      this._toggle();
    };
    this._keydownHandler = (e: KeyboardEvent): void => {
      if (e.key === ' ') {
        e.preventDefault();
        this._toggle();
      }
      // Enter is a no-op for checkboxes.
    };

    this.addEventListener('click', this._clickHandler);
    this.addEventListener('keydown', this._keydownHandler);
  }
}

if (!customElements.get('qds-checkbox')) {
  customElements.define('qds-checkbox', QdsCheckbox);
}
